import { isAction, type Middleware } from '@reduxjs/toolkit';
import storage from '../util/storage';
import { hydrate, ScriptStudioBuffersState } from './ScriptStudioBuffersSlice';

const STORAGE_KEY = 'scriptStudioBuffers';
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
const BUFFERS_ACTION_PREFIX = 'scriptStudioBuffers/';
const HYDRATE_ACTION_TYPE = hydrate.type;

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof localStorage !== 'undefined';
}

export function loadBuffersFromStorage(): ScriptStudioBuffersState | undefined {
  if (!isBrowser()) return undefined;
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return undefined;
    const data = JSON.parse(raw) as ScriptStudioBuffersState;
    if (!data || typeof data !== 'object') return undefined;
    return pruneOldEntries(data);
  } catch {
    return undefined;
  }
}

// Removes entries that have not been edited in the last 30 days.
// This is to prevent the localStorage from growing too large in cases where
// the user has many drafts and never renders/deletes them.
function pruneOldEntries(
  buffers: ScriptStudioBuffersState,
  nowMs: number = Date.now()
): ScriptStudioBuffersState {
  const cutoff = nowMs - THIRTY_DAYS_MS;
  const prunedById: ScriptStudioBuffersState['byId'] = {};
  const entries = Object.entries(buffers?.byId ?? {});
  for (const [id, value] of entries) {
    const last =
      typeof value?.lastEditedAt === 'number' ? value.lastEditedAt : 0;
    if (last >= cutoff) {
      prunedById[id] = value;
    }
  }
  return { byId: prunedById };
}

function isBuffersPersistAction(action: unknown): boolean {
  return (
    isAction(action) &&
    typeof action.type === 'string' &&
    action.type.startsWith(BUFFERS_ACTION_PREFIX) &&
    action.type !== HYDRATE_ACTION_TYPE
  );
}

/**
 * Persist script-studio drafts to localStorage.
 * Only runs for scriptStudioBuffers actions (not every Redux action).
 */
export const studioDraftLocalStorageMiddleware: Middleware = storeApi => {
  const persistBuffers = () => {
    if (!isBrowser()) return;
    const state = storeApi.getState();
    const buffers = state?.scriptStudioBuffers as
      | ScriptStudioBuffersState
      | undefined;
    if (!buffers) return;
    const prunedBuffers = pruneOldEntries(buffers);
    storage.setItem(STORAGE_KEY, JSON.stringify(prunedBuffers));
  };

  return next => action => {
    const result = next(action);

    if (!isBuffersPersistAction(action)) {
      return result;
    }

    persistBuffers();

    return result;
  };
};

export function createHydrateBuffersAction() {
  const loaded = loadBuffersFromStorage();
  if (!loaded) return undefined;
  return hydrate(loaded);
}
